import { Component, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { debounceTime, filter, Subject } from 'rxjs';
export interface AddressResult {
  displayName: string | null | undefined;
  formattedAddress: string | null | undefined;
  location: {
    lat: number | null | undefined;
    lng: number | null | undefined;
  };
}

@Component({
  selector: 'app-address-input-google',
  template: `
    <form nz-form [formGroup]="formGroup" [nzLayout]="'vertical'">
      <nz-form-item class="m-0">
        <nz-form-control [nzErrorTip]="addressError">
            <nz-select
              nzPlaceHolder="e.g. 123 Main St, New York"
              nzShowSearch
              nzServerSearch
              nzAllowClear
              [nzOptionHeightPx]="70"
              [nzOptionOverflowSize]="5"
              formControlName="text"
              (nzOnSearch)="onSearch($event)"
              [nzNotFoundContent]="notFoundContent">
            @if (options.length > 0) {
                @for (o of options; track $index) {
                  <nz-option [nzValue]="o" [nzLabel]="o.formattedAddress ?? ''" nzCustomContent>
                    <div class="flex flex-col gap-1 py-2 px-1">
                      <div class="flex items-center gap-2">
                        <nz-tag nzColor="geekblue" nzSize="small" class="rounded-full text-xs px-2 py-1">
                          <i nz-icon nzType="home" class="mr-1"></i>
                          <span class="font-semibold">{{ o?.displayName || 'No name' }}</span>
                        </nz-tag>
                        <span *ngIf="o?.location?.lat && o?.location?.lng" class="text-xs text-gray-400 ml-2">
                          <i nz-icon nzType="environment" nzTheme="outline"></i>
                          {{ o.location.lat }}, {{ o.location.lng }}
                        </span>
                      </div>
                      <div class="text-xs text-gray-700 truncate pl-1">
                        <i nz-icon nzType="pushpin" class="mr-1"></i>
                        {{ o?.formattedAddress || 'No address available' }}
                      </div>
                    </div>
                  </nz-option>
                }
            }
            </nz-select>
        </nz-form-control>
        <ng-template #addressError let-control>
        @if(control.errors?.['required']) { Required! }
        @if(control.errors?.['maxlength']) { Maximum 100 characters allowed! }
      </ng-template>
      </nz-form-item>
    </form>
    <ng-template #notFoundContent>
        <nz-empty nzNotFoundImage="simple" nzNotFoundContent="Type to search for addresses"></nz-empty>
    </ng-template>
  `,
})
export class AddressInputGoogleComponent implements OnInit, OnChanges {
  formGroup = new FormGroup({
    text: new FormControl<AddressResult | string>('', [Validators.required, Validators.maxLength(100)]),
  });
  options: AddressResult[] = [];
  loading = false;
  private searchSubject = new Subject<string>();
  @Output() onSearchEvent = new EventEmitter<any>();
  @Input() initialValue: string = '';
  
  constructor() { }

  ngOnInit(): void {
    // Set initial value if provided
    if (this.initialValue) {
      this.setInitialAddress(this.initialValue);
    }
    this.searchSubject
      .pipe(debounceTime(1000), filter(Boolean))
      .subscribe((value) => {
        this.getPlaces(value);
        this.loading = false;
      });

    this.formGroup.get('text')?.valueChanges.pipe(filter(Boolean)).subscribe((value) => {
      // Emit AddressResult object when selected from dropdown
      if (typeof value === 'object' && value !== null && 'formattedAddress' in value) {
        this.onSearchEvent.emit(value);
      }
    });
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['initialValue'] && changes['initialValue'].currentValue && !changes['initialValue'].firstChange) {
      this.setInitialAddress(changes['initialValue'].currentValue);
    }
  }

  private setInitialAddress(address: string): void {
    if (address) {
      const addressResult: AddressResult = {
        displayName: address,
        formattedAddress: address,
        location: {
          lat: null,
          lng: null,
        }
      };
      this.options = [addressResult];
      this.formGroup.get('text')?.setValue(addressResult, { emitEvent: false });
    }
  }

  onSearch(value: string): void {
    if (!value) {
      if (this.initialValue) {
        const addressResult: AddressResult = {
          displayName: this.initialValue,
          formattedAddress: this.initialValue,
          location: { lat: null, lng: null }
        };
        this.options = [addressResult];
      } else {
        this.options = [];
      }
      return;
    }
    this.searchSubject.next(value);
  }

  /**
   * Uses the new google.maps.places.Place API instead of the deprecated PlacesService.
   * See: https://developers.google.com/maps/documentation/javascript/places-migration-overview
   */
  protected async getPlaces(value: string): Promise<void> {
    if (!window.google?.maps?.places?.Place) {
      console.error('google.maps.places.Place API is not available. Please ensure you are using the new Places API (New).');
      return;
    }
    try {
      const request: google.maps.places.SearchByTextRequest = {
        textQuery: value,
        fields: ['displayName', 'location', 'formattedAddress'],
      };
      const response: { places: google.maps.places.Place[] } = await google.maps.places.Place.searchByText(request)
      if (response.places.length) {
        this.options = response.places.map((place: google.maps.places.Place) => (
          {
            displayName: place.displayName || '',
            formattedAddress: place.formattedAddress || '',
            location: {
              lat: place.location?.lat() || null,
              lng: place.location?.lng() || null,
            }
          }
        ));
      }
    } catch (error) {
      console.error('Error fetching places:', error);
    }
  }
}